/**
 * Classes: the URL picks the screen, the menu links to each.
 *   /micro-class-builder          → New class (default)
 *   /micro-class-builder/library  → Classes (the library)
 * "Who picks the skills" (the server's discovery mode) lives inside New
 * class's first step since brief 4 (Nino approved 6 Oct); it stays here
 * so resuming a draft from the library can set it.
 */
import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MCBTab } from '@/types/micro-class-builder';

const ClassCreator = lazy(() => import('./tabs/ClassCreator'));
const ClassLibrary = lazy(() => import('./tabs/ClassLibrary'));

const BASE = '/micro-class-builder';

function TabLoader() {
  return (
    <div className="flex items-center justify-center h-48">
      <div className="flex flex-col items-center gap-2">
        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </div>
    </div>
  );
}

export default function MicroClassBuilder() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [resumeDraftId, setResumeDraftId] = useState<string | null>(null);

  const activeTab: MCBTab = useMemo(() => {
    const segment = pathname.replace(BASE, '').replace(/^\//, '').split('/')[0];
    return segment === 'library' ? 'library' : 'create';
  }, [pathname]);

  const handleResumeDraft = useCallback((draftId: string, isDiscovery: boolean) => {
    setResumeDraftId(draftId);
    setDiscoveryMode(isDiscovery);
    navigate(BASE);
  }, [navigate]);


  return (
    <Suspense fallback={<TabLoader />}>
      {activeTab === 'create' && (
        <ClassCreator
          discoveryMode={discoveryMode}
          onDiscoveryModeChange={setDiscoveryMode}
          resumeDraftId={resumeDraftId}
          onResumeConsumed={() => setResumeDraftId(null)}
        />
      )}
      {activeTab === 'library' && <ClassLibrary onResumeDraft={handleResumeDraft} />}
    </Suspense>
  );
}
